/**
 * Per-DOF range / lock editor — the affordance that turns a bare design degree-of-freedom into an
 * optimization variable (DESIGN §8.A "ParamSpec on every design DOF"). Each row shows the live value,
 * a min/max box (the optimizer's bound), and a lock toggle. A locked DOF is held fixed during the
 * inverse-design / optimize loop — this is MSES-style *mixed-inverse* (only the unlocked DOFs under the
 * targeted surface move; the rest are pinned).
 *
 * Presentational only: it owns no state, emits `onChange(key, {min, max, locked})`, and is themed via
 * the shared `useHighliftTheme()` palette so it tracks Thread's light/dark toggle exactly like
 * `FoilControls` (whose compact label / value / row metrics it deliberately mirrors).
 */
import { useId } from 'react';
import { useHighliftTheme, type HighliftPalette } from './useHighliftTheme';

/** Font stacks (shared with `FoilControls`). */
const FONT_STACK = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** One editable degree-of-freedom (a packed `ParamSpec`, value at its live design position). */
export interface RangeSpec {
  /** Stable DOF key (e.g. `vane_te_z`) — round-trips through `pack`/`unpack`. */
  key: string;
  /** Human label for the row. */
  label: string;
  /** Current (live) value of the DOF. */
  value: number;
  /** Lower optimization bound. */
  min: number;
  /** Upper optimization bound. */
  max: number;
  /** Held fixed (mixed-inverse) when true. */
  locked?: boolean;
}

/** The bound/lock patch emitted for a single DOF. */
export interface RangePatch {
  min: number;
  max: number;
  locked: boolean;
}

export interface RangeControlsProps {
  /** The ranged DOFs to edit (one row each). */
  specs: RangeSpec[];
  /** Emitted when a row's bound or lock changes. */
  onChange: (key: string, patch: RangePatch) => void;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '3px 0',
};

function labelStyle(p: HighliftPalette): React.CSSProperties {
  return { flex: '0 0 120px', fontSize: 12, color: p.ink3, fontFamily: FONT_STACK };
}

function valueStyle(p: HighliftPalette): React.CSSProperties {
  return { flex: '0 0 48px', textAlign: 'right', fontSize: 11, color: p.ink, fontFamily: MONO_STACK };
}

function boundInputStyle(p: HighliftPalette, disabled: boolean): React.CSSProperties {
  return {
    flex: '1 1 0',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    fontSize: 11,
    fontFamily: MONO_STACK,
    color: disabled ? p.ink4 : p.ink,
    background: disabled ? p.surface2 : p.surface,
    border: `1px solid ${p.line}`,
    borderRadius: 4,
    padding: '2px 5px',
    accentColor: p.primary,
  };
}

/** Parse a numeric input, ignoring blank / non-finite intermediate states. */
function parseNum(raw: string): number | null {
  if (raw.trim() === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

/** A single DOF range row: live value · min · max · lock toggle. */
function RangeRow({
  spec,
  onChange,
  palette,
}: {
  spec: RangeSpec;
  onChange: (key: string, patch: RangePatch) => void;
  palette: HighliftPalette;
}) {
  const minId = useId();
  const maxId = useId();
  const locked = spec.locked ?? false;
  const dp = Math.abs(spec.max - spec.min) < 1 ? 3 : 2;

  const emit = (patch: Partial<RangePatch>) =>
    onChange(spec.key, { min: spec.min, max: spec.max, locked, ...patch });

  return (
    <div style={rowStyle}>
      <span style={labelStyle(palette)}>{spec.label}</span>
      <span style={valueStyle(palette)}>{spec.value.toFixed(dp)}</span>

      <label htmlFor={minId} style={{ fontSize: 10, color: palette.ink3, fontFamily: FONT_STACK }}>
        min
      </label>
      <input
        id={minId}
        type="number"
        aria-label={`${spec.label} min`}
        value={spec.min}
        disabled={locked}
        onChange={(e) => {
          const v = parseNum(e.target.value);
          if (v !== null) emit({ min: v });
        }}
        style={boundInputStyle(palette, locked)}
      />
      <label htmlFor={maxId} style={{ fontSize: 10, color: palette.ink3, fontFamily: FONT_STACK }}>
        max
      </label>
      <input
        id={maxId}
        type="number"
        aria-label={`${spec.label} max`}
        value={spec.max}
        disabled={locked}
        onChange={(e) => {
          const v = parseNum(e.target.value);
          if (v !== null) emit({ max: v });
        }}
        style={boundInputStyle(palette, locked)}
      />

      <button
        type="button"
        aria-label={`${locked ? 'Unlock' : 'Lock'} ${spec.label}`}
        aria-pressed={locked}
        title={locked ? 'Locked — held fixed (mixed-inverse)' : 'Unlocked — free to optimize'}
        onClick={() => emit({ locked: !locked })}
        style={{
          flex: '0 0 auto',
          fontSize: 12,
          lineHeight: 1,
          cursor: 'pointer',
          padding: '3px 6px',
          borderRadius: 4,
          fontFamily: FONT_STACK,
          color: locked ? palette.onPrimary : palette.ink3,
          background: locked ? palette.primary : 'transparent',
          border: `1px solid ${locked ? palette.primary : palette.line}`,
        }}
      >
        {locked ? '\u{1F512}' : '\u{1F513}'}
      </button>
    </div>
  );
}

export function RangeControls({ specs, onChange }: RangeControlsProps) {
  const palette = useHighliftTheme();

  return (
    <div
      style={{
        background: palette.surface,
        border: `1px solid ${palette.line}`,
        borderRadius: 8,
        padding: '10px 14px 14px',
        fontFamily: FONT_STACK,
        color: palette.ink,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: palette.ink,
          fontFamily: FONT_STACK,
          margin: '0 0 4px',
          paddingBottom: 4,
          borderBottom: `1px solid ${palette.line}`,
        }}
      >
        Optimization ranges
      </div>
      <div style={{ fontSize: 11, lineHeight: 1.4, color: palette.ink3, fontFamily: FONT_STACK, margin: '0 0 6px' }}>
        Bounds the optimizer / inverse-design loop may move each degree of freedom within. Lock a DOF to hold it
        fixed (mixed-inverse) — only unlocked DOFs move.
      </div>

      {specs.length === 0 ? (
        <div style={{ fontSize: 11, color: palette.ink4, fontFamily: FONT_STACK, padding: '6px 0' }}>
          No ranged degrees of freedom yet.
        </div>
      ) : (
        specs.map((spec) => (
          <RangeRow key={spec.key} spec={spec} onChange={onChange} palette={palette} />
        ))
      )}
    </div>
  );
}
